import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingsActions } from 'src/app/core/settings/settings.actions'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { Constants, UrlMatcherName } from 'src/app/shared/constants'

import { Component, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import {
  CondOperator,
  QuerySortOperator,
  RequestQueryBuilder
} from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'

import moment from 'moment-mini'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { Location } from 'src/app/features/location/location'
import { locationActions } from 'src/app/features/location/location.actions'
import { locationSelectors } from 'src/app/features/location/location.selectors'
import {
  ProductGroup,
  ProductGroupType
} from 'src/app/features/product/product-group/product-group'
import { productGroupActions } from 'src/app/features/product/product-group/product-group.actions'
import { productGroupSelectors } from 'src/app/features/product/product-group/product-group.selectors'
import { UserAccessRightService } from '../../../../shared/services/user-access-rights.service'
import { memberSelectors } from '../../../member/member.selectors'
import { Organization } from '../../../organization/organization'
import { organizationActions } from '../../../organization/organization.actions'
import { organizationSelectors } from '../../../organization/organization.selectors'
import { Product } from '../../../product/product'
import { productActions } from '../../../product/product.actions'
import { productSelectors } from '../../../product/product.selectors'
import {
  SerieProposal,
  SerieProposalStatus,
  SerieProposalType
} from '../serie-proposal'
import { SerieProposalItem } from '../serie-proposal-item/serie-proposal-item'
import { serieProposalItemSelectors } from '../serie-proposal-item/serie-proposal-item.selectors'
import { serieProposalActions } from '../serie-proposal.actions'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import DEBOUNCED_TIME = Constants.DEBOUNCED_TIME
import { selectLasPageVisited } from 'src/app/shared/components/breadcrumb/breadcrumb.selectors'
import { createUrlMatcher } from 'src/app/shared/until'

export enum SerieProposalTabs {
  ALL = 0,
  REQUESTED = 1,
  ACCEPTED = 2,
  COUNTER_PROPOSAL = 3,
  DECLINED = 4,
  DEFAULT = 5
}

export enum SerieProposalViews {
  CALENDAR = 'calendar',
  LIST = 'list'
}

@UntilDestroy()
@Component({
  selector: 'serie-proposal-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss']
})
export class SerieProposalIndexComponent implements OnInit {
  private prevSeriesProposalSearchParams: any = null
  private prevPropductsSearchParams: any = null
  public accessRights = AccessRights
  public total: number = 0
  public page: number = 1
  public limit: number = 25
  public pageCount: number = 0
  public form: UntypedFormGroup
  public searchTerm$ = new Subject<string>()
  public searchTerm = ''
  public isLoading$: Observable<boolean>
  public serieProposals: SerieProposal[] = []
  private fetchedSerieProposals: SerieProposal[] = []
  public serieProposalItems$: Observable<SerieProposalItem[]>
  public selectedFilters$: Observable<any>
  public allProducts$: Observable<Product[]>
  public bookableProducts$: Observable<Product[]>
  public productEntities$: Observable<Dictionary<Product>>
  public serieProposalTabs = SerieProposalTabs
  public serieProposalViews = SerieProposalViews
  public organizations$: Observable<Organization[]>
  public serieProposalEntities: Dictionary<SerieProposal>
  public serieProposalItemEntities: Dictionary<SerieProposalItem>

  public field: string = 'id'
  public order = 'DESC'
  public bsModalRef: BsModalRef
  public selectedBookableProductIdsSubject = new BehaviorSubject<number[]>([])
  public selectedProductGroupIds$: Observable<number[]>
  public selectedBookableProductIds$ =
    this.selectedBookableProductIdsSubject.asObservable()
  private dependantProductIdsSubject = new BehaviorSubject<number[]>([])
  public dependantProductIds$ = this.dependantProductIdsSubject.asObservable()
  public locations$: Observable<Location[]>
  public productGroups$: Observable<ProductGroup[]>
  public targetedProductsIds$: Observable<any>
  public dependantProductsMap: { [key: number]: number[] } = {}
  public activeTab = new BehaviorSubject<SerieProposalTabs>(
    SerieProposalTabs.DEFAULT
  )
  public getActiveTab$() {
    return this.activeTab.asObservable()
  }
  private targetedProductsIds: number[] = []
  private bookableProductIdsWithDependantProductIds: number[] = []

  public activeView = new BehaviorSubject<SerieProposalViews>(
    SerieProposalViews.CALENDAR
  )
  public getActiveView$() {
    return this.activeView.asObservable()
  }

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private actions$: Actions,
    private fb: UntypedFormBuilder,
    public userAccessRightService: UserAccessRightService,
    public notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.store
      .select(settingSelectors.selectState)
      .pipe(take(1))
      .subscribe((settings) => {
        if (
          settings.hasOwnProperty('resetFiltersFlag') &&
          settings['resetFiltersFlag'] === true
        ) {
          this.store.dispatch(
            settingsActions.resetFilter({ id: 'selectedFilters' })
          )
          this.store.dispatch(
            settingsActions.resetFilter({ id: 'selectedEmployeeFilters' })
          )
          this.store.dispatch(
            settingsActions.setResetFiltersFlag({ value: false })
          )
        }
      })
    this.activeView.pipe(take(1)).subscribe((activeView) => {
      if (activeView === SerieProposalViews.CALENDAR) {
        this.limit = 500
      }
    })
    this.store.dispatch(
      BreadCrumbActions.initHistory({
        pageName: 'serieProposalModule.serieProposals',
        url: '/bookings',
        titleName: ['serieProposalModule.serieProposals']
      })
    )

    this.isLoading$ = combineLatest([
      this.store.select(productSelectors.selectIsLoading),
      this.store.select(serieProposalSelectors.selectIsLoading)
    ]).pipe(map(([a, b]) => a || b))

    this.locations$ = this.store.select(locationSelectors.selectAll)
    this.allProducts$ = this.store.select(productSelectors.selectAll)
    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.bookableProducts$ = this.allProducts$.pipe(
      map((product) =>
        product.filter((product) => product.bookable && product.isActive)
      )
    )

    this.store.dispatch(
      productGroupActions.fetchMany({
        params: RequestQueryBuilder.create({
          join: [{ field: 'products' }],
          search: {
            type: ProductGroupType.REPORT_GROUP
          }
        })
      })
    )

    this.productGroups$ = this.store
      .select(productGroupSelectors.selectAll)
      .pipe(
        map((productGroups) =>
          productGroups.filter(
            (productGroup) => productGroup.type == ProductGroupType.REPORT_GROUP
          )
        )
      )

    this.organizations$ = this.store.select(organizationSelectors.selectAll)

    this.form = this.fb.group({
      selectedProductGroupIds: [null, []],
      selectedBookableProductIds: [[], []],
      selectedOrganizationIds: [[], []],
      selectedCategories: [[], []],
      dateRange: [
        [
          moment().subtract(3, 'months').startOf('day').toDate(),
          moment().endOf('day').toDate()
        ],
        []
      ]
    })

    this.fetchSerieProposals()

    this.selectedFilters$ = this.store
      .select(settingSelectors.selectById, 'selectedFilters')
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        tap((item) => {
          this.form.controls.selectedBookableProductIds.setValue(
            item?.selectedBookableProductIds
          )
          this.form.controls.selectedProductGroupIds.setValue(
            item?.selectedProductGroupIds
          )
        })
      )

    this.selectedBookableProductIds$ =
      this.form.controls.selectedBookableProductIds.valueChanges

    this.selectedProductGroupIds$ =
      this.form.controls.selectedProductGroupIds.valueChanges

    const formChanges$ = this.form.valueChanges.pipe(
      startWith(this.form.value),
      debounceTime(200),
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
      )
    )

    this.targetedProductsIds$ = combineLatest([
      this.selectedBookableProductIds$,
      this.selectedProductGroupIds$,
      this.selectedFilters$,
      this.store.select(productGroupSelectors.selectEntities)
    ]).pipe(
      map(
        ([
          selectedBookableProductIds,
          selectedProductGroupIds,
          selectedFilters,
          productGroupEntities
        ]) => {
          selectedProductGroupIds = selectedProductGroupIds ?? []
          let targetedProductGroupProductIds = []
          selectedProductGroupIds?.map(
            (productGroupId) =>
              (targetedProductGroupProductIds = [
                ...targetedProductGroupProductIds,
                ...(productGroupEntities[productGroupId]?.products ?? [])
              ])
          )

          this.targetedProductsIds = Array.from(
            new Set([
              ...(selectedBookableProductIds ?? []),
              ...targetedProductGroupProductIds
            ])
          )
          return this.targetedProductsIds
        }
      )
    )

    combineLatest([
      this.selectedFilters$,
      this.targetedProductsIds$,
      formChanges$,
      this.getActiveView$()
    ])
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.store.select(settingSelectors.selectState)),
        debounceTime(DEBOUNCED_TIME)
      )
      .subscribe(
        ([
          [selectedFilters, targetedProductsIds, valueChanges, activeView],
          settings
        ]) => {
          const targetFilters = {
            selectedBookableProductIds:
              valueChanges.selectedBookableProductIds ?? [],
            selectedProductGroupIds: valueChanges.selectedProductGroupIds ?? [],
            selectedOrganizationIds: valueChanges.selectedOrganizationIds ?? [],
            selectedCategories: valueChanges.selectedCategories ?? []
          }
          let reloadProducts = false

          if (
            JSON.stringify(targetFilters) !== JSON.stringify(selectedFilters)
          ) {
            reloadProducts = true
            this.store.dispatch(
              settingsActions.setOneById({
                id: 'selectedFilters',
                value: targetFilters
              })
            )
          }

          // if (activeView != SerieProposalViews.LIST) {
          const allFiltersAreEmpty = Object.values(targetFilters).every(
            (filter) => Array.isArray(filter) && filter?.length === 0
          )

          if (allFiltersAreEmpty) {
            this.serieProposals = []
            this.selectedBookableProductIdsSubject.next([])
            this.dependantProductIdsSubject.next([])

            return
          }

          if (targetedProductsIds?.length || reloadProducts) {
            // first we need to fetch the products with dependant products
            this.fetchProducts(targetedProductsIds, reloadProducts)
            this.fetchSerieProposals()
          } else {
            this.fetchSerieProposals()
          }
          // }
        }
      )

    this.store
      .select(serieProposalSelectors.selectEntities)
      .pipe(untilDestroyed(this))
      .subscribe(
        (serieProposalEntities) =>
          (this.serieProposalEntities = serieProposalEntities)
      )

    this.store
      .select(serieProposalItemSelectors.selectEntities)
      .pipe(untilDestroyed(this))
      .subscribe(
        (serieProposalItemEntities) =>
          (this.serieProposalItemEntities = serieProposalItemEntities)
      )

    this.searchTerm$
      .pipe(distinctUntilChanged(), debounceTime(100), untilDestroyed(this))
      .subscribe((term) => {
        this.searchTerm = term
        this.page = 1
        this.fetchSerieProposals()
      })

    // when we switch to list view we have to filter out the serie proposals of the dependant products that were fetched on schedule view
    this.getActiveView$()
      .pipe(untilDestroyed(this))
      .subscribe((activeView) => {
        this.serieProposals = this.fetchedSerieProposals.filter(
          (serieProposal) =>
            activeView == this.serieProposalViews.LIST
              ? !this.form.value.selectedBookableProductIds?.length ||
                this.form.value.selectedBookableProductIds.includes(
                  serieProposal?.productId
                )
                ? true
                : false
              : true
        )
      })

    this.getActiveTab$()
      .pipe(untilDestroyed(this))
      .subscribe((activeTab) => {
        this.serieProposals = this.fetchedSerieProposals.filter(
          (serieProposal) =>
            !this.filterBySelectedTab(activeTab, serieProposal) ? false : true
        )
      })
    this.store
      .select(selectLasPageVisited)
      .pipe(take(1))
      .subscribe((selectLasPageVisited) => {
        const matchSerieProposal = createUrlMatcher(
          UrlMatcherName.SERIE_PROPOSAL,
          { id: 'number' }
        )
        if (
          selectLasPageVisited &&
          matchSerieProposal(selectLasPageVisited?.url) &&
          selectLasPageVisited.pageName
        ) {
          this.onClickSelectedView(
            selectLasPageVisited.pageName as SerieProposalViews
          )
        }
      })
  }

  public onSearchProductGroups(term: string) {
    this.store.dispatch(
      productGroupActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          },
          join: [{ field: 'products' }]
        })
      })
    )
  }
  public onSorted(fieldSort: string) {
    this.field = fieldSort.split('_')[0]
    this.order = fieldSort.split('_')[1].toUpperCase()
    this.page = 1
    this.fetchSerieProposals()
  }
  private fetchProducts(productIds: number[], reload: boolean = false) {
    const uniqueProductIds: number[] = Array.from(
      new Set([
        ...productIds,
        ...(this.serieProposals?.length
          ? this.serieProposals.flatMap((item) => [
              item.productId,
              item.linkedProductId
            ])
          : [])
      ])
    )
    if (
      this.areParamsChanged(
        {
          ...uniqueProductIds
        },
        this.prevPropductsSearchParams
      ) ||
      reload
    ) {
      if (uniqueProductIds?.length > 0) {
        this.store.dispatch(
          productActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                id: { [CondOperator.IN]: uniqueProductIds }
              },
              join: [
                { field: 'translations' },
                { field: 'dependantProducts' },
                { field: 'parentBookableProducts' }
              ]
            })
          })
        )
        this.actions$
          .pipe(
            ofType(productActions.upsertMany),
            take(1),
            catchError((error) => of(null))
          )
          .subscribe((action) => {
            if (action) {
              this.dependantProductsMap = {}
              let dependantProductIds: number[] = []
              for (const productId of productIds) {
                const dependantProductIdsOfProduct =
                  this.getDependantProductIdsByProductId(productId)

                this.dependantProductsMap[productId] =
                  dependantProductIdsOfProduct ?? []
                dependantProductIdsOfProduct.forEach((id) => {
                  if (
                    !dependantProductIds.includes(id) &&
                    !productIds.includes(id)
                  ) {
                    dependantProductIds.push(id)
                  }
                })
              }
              this.bookableProductIdsWithDependantProductIds = [
                ...productIds,
                ...dependantProductIds
              ]

              this.selectedBookableProductIdsSubject.next(productIds)
              this.dependantProductIdsSubject.next(dependantProductIds)

              this.fetchSerieProposals()
            }
          })
      }
    }
    this.prevPropductsSearchParams = {
      ...uniqueProductIds
    }
  }
  private areParamsChanged(newParams: any, oldParams: any): boolean {
    if (!oldParams) {
      return true
    }

    return JSON.stringify(newParams) !== JSON.stringify(oldParams)
  }
  private filterProposals(
    serieProposals: SerieProposal[],
    activeTab,
    productEntities,
    memberEntities,
    organizationEntities,
    activeView
  ): SerieProposal[] {
    return serieProposals
      .map((serieProposal) => {
        let filteredIds = []

        if (!this.filterBySelectedTab(activeTab, serieProposal)) {
          return null
        }

        if (
          activeView == this.serieProposalViews.CALENDAR &&
          !moment(serieProposal.createdAt).isBetween(
            moment(this.form.value.dateRange?.[0]).startOf('day'),
            moment(this.form.value.dateRange?.[1]).endOf('day')
          )
        ) {
          return null
        }

        // TODO change this filter to selectedProductGroupIds
        // if (
        //   this.form.value.selectedLocationIds?.length &&
        //   !this.form.value.selectedLocationIds.includes(
        //     productEntities[serieProposal.productId].locationId
        //   )
        // ) {
        //   return null
        // }

        if (activeView == this.serieProposalViews.LIST) {
          if (
            this.form.value.selectedBookableProductIds?.length &&
            !this.form.value.selectedBookableProductIds.includes(
              serieProposal?.productId
            )
          ) {
            return null
          }
        } else {
          if (
            this.bookableProductIdsWithDependantProductIds?.length &&
            !this.bookableProductIdsWithDependantProductIds.includes(
              serieProposal?.productId
            )
          ) {
            return null
          }
        }

        if (
          this.form.value.selectedOrganizationIds?.length &&
          !this.form.value.selectedOrganizationIds.includes(
            serieProposal.organizationId
          )
        ) {
          return null
        }

        if (this.form.value.selectedCategories?.length) {
          const member = memberEntities[serieProposal.customerPersonId]
          const organization =
            organizationEntities[serieProposal.organizationId]
          const category = organization?.category ?? member?.category ?? null

          if (!this.form.value.selectedCategories.includes(Number(category))) {
            return null
          }
        }

        if (!filteredIds.includes(serieProposal.id)) {
          filteredIds.push(serieProposal.id)
          return serieProposal
        }
        return null
      })
      .filter(Boolean)
  }

  public fetchSerieProposals(mandatoryReload: boolean = false) {
    this.getActiveView$()
      .pipe(take(1))
      .subscribe((activeView) => {
        const startDate = moment(this.form.value.dateRange?.[0])
        const endDate = moment(this.form.value.dateRange?.[1])
        let searchParams

        if (this.searchTerm?.length) {
          searchParams = {
            createdAt: {
              [CondOperator.BETWEEN]: [
                startDate.clone().toISOString(),
                endDate.clone().toISOString()
              ]
            },
            $or: [
              {
                id: {
                  [CondOperator.CONTAINS_LOW]: this.searchTerm
                }
              },
              {
                ['customer.firstName']: {
                  [CondOperator.CONTAINS_LOW]: this.searchTerm
                }
              },
              {
                ['customer.lastName']: {
                  [CondOperator.CONTAINS_LOW]: this.searchTerm
                }
              },
              {
                ['customer.email']: {
                  [CondOperator.CONTAINS_LOW]: this.searchTerm
                }
              }
            ]
          }

          // in list view we don't have to fetch the proposals of the dependant products
          if (activeView == this.serieProposalViews.LIST) {
            if (this.targetedProductsIds?.length) {
              searchParams['productId'] = {
                [CondOperator.IN]: this.targetedProductsIds
              }
            }
          } else {
            if (this.bookableProductIdsWithDependantProductIds?.length) {
              searchParams['productId'] = {
                [CondOperator.IN]:
                  this.bookableProductIdsWithDependantProductIds
              }
            }
          }

          if (this.form.value.selectedOrganizationIds?.length) {
            searchParams['organizationId'] = {
              [CondOperator.IN]: this.form.value.selectedOrganizationIds
            }
          }
          if (this.form.value.selectedCategories?.length) {
            searchParams['organization.category'] = {
              [CondOperator.IN]: this.form.value.selectedCategories
            }
          }
        } else {
          searchParams = {
            createdAt: {
              [CondOperator.BETWEEN]: [
                startDate.clone().toISOString(),
                endDate.clone().toISOString()
              ]
            }
          }

          // in list view we don't have to fetch the proposals of the dependant products
          if (activeView == this.serieProposalViews.LIST) {
            if (this.targetedProductsIds?.length) {
              searchParams['productId'] = {
                [CondOperator.IN]: this.targetedProductsIds
              }
            }
          } else {
            if (this.bookableProductIdsWithDependantProductIds?.length) {
              searchParams['productId'] = {
                [CondOperator.IN]:
                  this.bookableProductIdsWithDependantProductIds
              }
            }
          }

          if (this.form.value.selectedOrganizationIds?.length) {
            searchParams['organizationId'] = {
              [CondOperator.IN]: this.form.value.selectedOrganizationIds
            }
          }
          if (this.form.value.selectedCategories?.length) {
            searchParams['organization.category'] = {
              [CondOperator.IN]: this.form.value.selectedCategories
            }
          }
        }
        if (
          this.areParamsChanged(
            {
              ...searchParams,
              page: this.page,
              limit: this.limit,
              field: this.field,
              order: this.order
            },
            this.prevSeriesProposalSearchParams
          ) ||
          mandatoryReload
        ) {
          this.store.dispatch(
            serieProposalActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: searchParams,
                page: this.page,
                limit: this.limit,
                join: [
                  { field: 'customer' },
                  { field: 'organization' },
                  { field: 'messages' },
                  { field: 'serieProposalItems' },
                  { field: 'product' },
                  { field: 'linkedProduct' }
                ],
                sort: {
                  field: this.field,
                  order: this.order as QuerySortOperator
                }
              })
            })
          )

          this.actions$
            .pipe(
              ofType(serieProposalActions.upsertMany),
              withLatestFrom(
                this.getActiveTab$(),
                this.productEntities$,
                this.store.select(memberSelectors.selectEntities),
                this.store.select(organizationSelectors.selectEntities),
                this.getActiveView$()
              ),
              take(1),
              catchError((error) => of(null))
            )
            .subscribe(
              ([
                action,
                activeTab,
                productEntities,
                memberEntities,
                organizationEntities,
                activeView
              ]) => {
                if (!action) {
                  this.serieProposals = []
                  return
                }

                this.total = action.total
                this.pageCount = action.pageCount

                this.fetchedSerieProposals = action?.entities

                const filteredProposals = this.filterProposals(
                  this.fetchedSerieProposals,
                  activeTab,
                  productEntities,
                  memberEntities,
                  organizationEntities,
                  activeView
                )

                this.serieProposals =
                  activeView == this.serieProposalViews.CALENDAR
                    ? filteredProposals.filter(
                        (it) =>
                          it.details?.repeatableBookings?.length > 0 ||
                          it.details?.initialRequest?.repeatableBookings
                            ?.length > 0
                      )
                    : filteredProposals
              }
            )
        }
        this.prevSeriesProposalSearchParams = {
          ...searchParams,
          page: this.page,
          limit: this.limit,
          field: this.field,
          order: this.order
        }
      })
  }

  public onClickSelectedType(type: string) {
    this.form.controls.selectedType.patchValue(type)
  }

  public onClickRow(serieProposal: SerieProposal) {
    this.store.dispatch(
      BreadCrumbActions.initHistory({
        pageName: 'serieProposalModule.serieProposals',
        url: '/bookings',
        titleName: ['serieProposalModule.serieProposals']
      })
    )
    this.store.dispatch(
      BreadCrumbActions.setLastPageVisited({
        url: `/bookings/serie-proposals/${serieProposal.id}`,
        pageName: SerieProposalViews.LIST,
        titleName: ['serieProposalModule.serieProposals']
      })
    )

    this.router.navigate(['/bookings/serie-proposals', serieProposal.id])
  }

  public selectNewPage(page) {
    this.page = page
    this.fetchSerieProposals()
  }

  public selectNewLimit(limit) {
    this.limit = limit
    this.page = 1
    this.fetchSerieProposals()
  }

  public onSearchTerm(searchTerm) {
    if (searchTerm?.length > 0) {
      this.searchTerm$.next(searchTerm)
    } else {
      this.searchTerm = ''
      this.page = 1
      setTimeout(() => {
        this.fetchSerieProposals()
      }, 200)
    }
  }

  public onClickSelectedTab(tab: SerieProposalTabs): void {
    this.activeTab.next(tab)
  }
  private selectTab(view: SerieProposalViews) {
    this.actions$
      .pipe(ofType(serieProposalActions.upsertMany), take(1), timeout(1000))
      .subscribe((action) => {
        if (view == SerieProposalViews.CALENDAR) {
          this.onClickSelectedTab(this.serieProposalTabs.DEFAULT)
        } else {
          this.onClickSelectedTab(this.serieProposalTabs.ALL)
        }
      })
  }
  public onClickSelectedView(view: SerieProposalViews): void {
    if (view === SerieProposalViews.CALENDAR) {
      this.limit = 500
    } else {
      this.limit = 25
    }
    this.selectTab(view)
    this.fetchSerieProposals()
    this.activeView.next(view)
  }

  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          },
          join: [
            { field: 'translations' },
            { field: 'dependantProducts' },
            { field: 'dependantProducts.translations' }
          ]
        })
      })
    )
  }
  public setSerieProposalType(serieProposal) {
    let mappedSerieProposal = JSON.parse(JSON.stringify(serieProposal))
    mappedSerieProposal.forEach((sP) => {
      if (
        sP.details.initialRequest.repeatableBookings?.length > 0 ||
        sP.details.repeatableBookings?.length > 0
      ) {
        sP.type = SerieProposalType.REPEATABLE_BOOKING
      } else if (
        sP.details.initialRequest.customBookings?.length > 0 ||
        sP.details.customBookings?.length > 0
      ) {
        sP.type = SerieProposalType.CUSTOM_DATE
      }
    })

    return mappedSerieProposal
  }
  public onSearchLocation(term: string) {
    this.store.dispatch(
      locationActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          }
        })
      })
    )
  }

  public onSearchOrganizations(term: string) {
    this.store.dispatch(
      organizationActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          }
        })
      })
    )
  }

  private filterBySelectedTab(
    activeTab: SerieProposalTabs,
    serieProposal: SerieProposal
  ) {
    switch (activeTab) {
      case SerieProposalTabs.DEFAULT:
        return serieProposal.status != SerieProposalStatus.DECLINED
      case SerieProposalTabs.ALL:
        return serieProposal
      case SerieProposalTabs.REQUESTED:
        return serieProposal.status == SerieProposalStatus.REQUESTED

      case SerieProposalTabs.COUNTER_PROPOSAL:
        return serieProposal.status == SerieProposalStatus.COUNTER_PROPOSAL

      case SerieProposalTabs.ACCEPTED:
        return serieProposal.status == SerieProposalStatus.ACCEPTED

      case SerieProposalTabs.DECLINED:
        return serieProposal.status == SerieProposalStatus.DECLINED
    }
  }

  private getDependantProductIdsByProductId(productId: number): number[] {
    let dependantProductIds = []
    this.store
      .select(productSelectors.selectEntities)
      .pipe(untilDestroyed(this))
      .subscribe((productEntities) => {
        if (!productEntities?.[productId]?.dependantProducts) {
          return
        }

        dependantProductIds = productEntities?.[productId]?.dependantProducts
      })

    return dependantProductIds
  }

  private isFiltersEmpty(selectedProductGroupIds, selectedBookableProductIds) {
    return (
      !selectedProductGroupIds?.length && !selectedBookableProductIds?.length
    )
  }
}
